import React, { useState } from 'react';

import ArticleListingItem from '$partials/listings/article-listing-item';
import { useLocale } from '$contexts/locale';
import { useArticleListing } from '$contexts/article-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base';
import { useAjaxListing } from '$hooks/ajax-listing';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleArticleGrid}
 */

const ModuleArticleGrid = ({
    header,
    eyebrow,
    bodyCopy,
    articleCategories,
    defaultFilterLabel,
    isArticlePage,
}) => {
    const { listing, conceptId } = useArticleListing();
    const [category, setCategory] = useState(listing.filters.category?.value || null);

    const { translate: t } = useLocale();

    const ajaxListing = useAjaxListing({
        infinite: true,
        items: listing.items,
        path: '/api/v1/listings',
        page: listing.pageInfo.page,
        totalItems: listing.pageInfo.totalItems,
        totalPages: listing.pageInfo.totalPages,
        query: {
            conceptId,
            category,
            listingPageType: 'ArticleListingPage'
        }
    });

    return (

        <section className='relative py-40 lg:py-80 overflow-hidden'>
            <div className='layout-container layout-site-outer-padding'>
                <div className='flex flex-col sm:flex-row gap-y-24 justify-between col-wrapper'>
                    <div>

                        <div className='col-padding sm:w-10/12 md:w-7/12 lg:w-6/12'>
                            {eyebrow && (
                                <p
                                    className='type-style-eyebrow text-text-accent mb-12'>
                                    {eyebrow}
                                </p>
                            )}

                            <h2 className={`${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} text-text-primary mb-10`}>
                                {header}
                            </h2>

                            {bodyCopy && (
                                <p className='type-style-body text-text-secondary'>
                                    {bodyCopy}
                                </p>
                            )}
                        </div>
                        {(articleCategories?.items && articleCategories?.items?.length > 0) && (
                            <div className='pt-40'>
                                {/* Filters */}
                                <div className='overflow-y-clip overflow-x-auto no-scrollbar max-w-fit'>
                                    <ul
                                        className='list-none flex pb-4 gap-x-16 type-style-body-small'>
                                        {defaultFilterLabel && (
                                            <li
                                                className={'group inline-block relative flex-shrink-0 pb-3 filter-tab'}>
                                                <button
                                                    className='pb-3 min-w-50 text-left'
                                                    onClick={() => setCategory(null)}
                                                    type='button'>
                                                    {defaultFilterLabel}
                                                </button>
                                                <div
                                                    aria-hidden={true}
                                                    className='filter-tab-row__underline '
                                                    data-selected={category === null} />
                                            </li>
                                        )}
                                        {articleCategories.items.map((item) => {
                                            if (!item) { return null; }
                                            return (
                                                <li
                                                    className={'group inline-block relative flex-shrink-0 pb-3 filter-tab'}
                                                    key={item.value}>
                                                    <button
                                                        className='pb-3 min-w-50 text-left'
                                                        onClick={() => setCategory(item.value)}
                                                        type='button'>
                                                        {item.label}
                                                    </button>
                                                    <div
                                                        aria-hidden={true}
                                                        className='filter-tab-row__underline'
                                                        data-selected={item.value === category}/>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Listing items */}
                <div className='mt-40 -mx-layout-col'>
                    <AjaxListingContext.Provider value={ajaxListing}>
                        <AjaxListingContext.Consumer>
                            {(listingState) => (
                                <>
                                    <section
                                        aria-busy={listingState.loading}
                                        aria-label='Articles'
                                        className='flex flex-wrap gap-y-40'
                                        role='feed'>
                                        {/* Listing feed */}
                                        {listingState.items?.length > 0 && (
                                            <>
                                                {listingState.items.map((item, i) => {
                                                    return (
                                                        <article
                                                            aria-label={item.title}
                                                            aria-posinset={i + 1}
                                                            className='w-full sm:w-1/2 md:w-1/3  px-layout-col'
                                                            key={item.id}>
                                                            <ArticleListingItem
                                                                {...item}/>

                                                        </article>
                                                    );
                                                })}
                                                <div className='px-layout-container py-10 w-full'>
                                                    <InfinitePagination>
                                                        {'Loading...'}
                                                    </InfinitePagination>
                                                </div>
                                            </>
                                        )}
                                        {/* No result */}
                                        {listingState.items?.length === 0 && (
                                            <div className='px-layout-container px-layout-col'>
                                                <p className='type-style-body text-text-secondary'>
                                                    {t('LISTING_NO_RESULT_MESSAGE')}
                                                </p>
                                            </div>
                                        )}
                                    </section>
                                </>
                            )}
                        </AjaxListingContext.Consumer>
                    </AjaxListingContext.Provider>
                </div>
            </div>
        </section>

    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default ModuleArticleGrid;
